import creditCardType from 'credit-card-type';
import { postalCode } from 'card-validator';
import { EMAIL_REGEXP, PHONE_NUMBER_REGEXP } from '../../constants';

export type FormElement = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

export type FormElementNodeName = 'INPUT' | 'SELECT' | 'TEXTAREA';

export type FormElementNodeNames = {
  [TNodeName in FormElementNodeName]: boolean;
};

export type FormElements = {
  [elementName: string]: FormElement;
};

export type FormValues = {
  [elementName: string]: string | boolean;
};

export type ValidationType =
  | 'creditCardNumber'
  | 'custom'
  | 'differentFrom'
  | 'email'
  | 'maxAmount'
  | 'maxLength'
  | 'minLength'
  | 'pattern'
  | 'phoneNumber'
  | 'postalCode'
  | 'required'
  | 'sameAs';

type FormError = { element: FormElement; message?: string; type: ValidationType };

export type FormErrors = { [elementName: string]: FormError };

export type Validation<TValue> = {
  message?: string;
  value: TValue;
};

type ElementValidation = {
  creditCardNumber?: Validation<boolean>;
  custom?: Validation<(value: string) => boolean>;
  differentFrom?: Validation<string>;
  email?: Validation<boolean>;
  maxAmount?: Validation<number | undefined>;
  maxLength?: Validation<number>;
  minLength?: Validation<number>;
  pattern?: Validation<RegExp>;
  phoneNumber?: Validation<boolean>;
  postalCode?: Validation<boolean>;
  required?: Validation<boolean>;
  sameAs?: Validation<string>;
};

export type Validations = {
  [elementName: string]: ElementValidation;
};

const formElementNodeNames: FormElementNodeNames = { INPUT: true, SELECT: true, TEXTAREA: true };

export const getFormElementArray = (form: HTMLFormElement): FormElement[] => {
  return Array.from(form.elements).filter(el => {
    return formElementNodeNames[el.nodeName as FormElementNodeName];
  }) as FormElement[];
};

const getElementName = (element: FormElement): string => {
  return element.name || element.id;
};

const isElementCheckbox = (element: FormElement): element is HTMLInputElement =>
  element.type === 'checkbox';

const getElementValue = (element: FormElement): string | boolean => {
  return isElementCheckbox(element) ? element.checked : element.value;
};

export const getFormElements = (formElementArray: FormElement[]): FormElements => {
  return formElementArray.reduce(
    (formElements: FormElements, element: FormElement): FormElements => {
      const elementName = getElementName(element);

      if (elementName) {
        formElements[elementName] = element;
      }

      return formElements;
    },
    {}
  );
};

export const getFormValues = (formElements: FormElements): FormValues => {
  return Object.keys(formElements).reduce((formValues: FormValues, name: string): FormValues => {
    const element = formElements[name];

    formValues[name] = getElementValue(element);

    return formValues;
  }, {});
};

export const validateCreditCardNumber = (number: string) =>
  creditCardType(number).some(type =>
    type.lengths?.some(length => number.replace(/ /g, '').length === length)
  );

export const validateFormValue = (
  value: string,
  validation: ElementValidation
): Omit<FormError, 'element'> | null => {
  if (validation.required?.value && !value) {
    return {
      message: validation.required.message,
      type: 'required'
    };
  }

  if (!value) {
    return null;
  }

  if (validation.minLength?.value && value.length < validation.minLength.value) {
    return {
      message: validation.minLength.message,
      type: 'minLength'
    };
  }

  if (validation.maxLength?.value && value.length > validation.maxLength.value) {
    return {
      message: validation.maxLength.message,
      type: 'maxLength'
    };
  }

  if (validation.email?.value && !new RegExp(`^${EMAIL_REGEXP.source}$`).test(value)) {
    return {
      message: validation.email.message,
      type: 'email'
    };
  }

  if (validation.phoneNumber?.value && !new RegExp(`^${PHONE_NUMBER_REGEXP.source}$`).test(value)) {
    return {
      message: validation.phoneNumber.message,
      type: 'phoneNumber'
    };
  }

  if (validation.pattern?.value && !validation.pattern.value.test(value)) {
    return {
      message: validation.pattern.message,
      type: 'pattern'
    };
  }

  if (validation.creditCardNumber?.value && !validateCreditCardNumber(value)) {
    return {
      message: validation.creditCardNumber.message,
      type: 'creditCardNumber'
    };
  }

  if (validation.maxAmount?.value && Number(value) > Number(validation.maxAmount.value)) {
    return {
      message: validation.maxAmount.message,
      type: 'maxAmount'
    };
  }

  if (validation.differentFrom?.value && validation.differentFrom.value === value) {
    return {
      message: validation.differentFrom.message,
      type: 'differentFrom'
    };
  }

  if (validation.sameAs?.value && validation.sameAs.value !== value) {
    return {
      message: validation.sameAs.message,
      type: 'sameAs'
    };
  }

  if (validation.postalCode?.value && !postalCode(value).isValid) {
    return {
      message: validation.postalCode.message,
      type: 'postalCode'
    };
  }

  if (validation.custom?.value && !validation.custom.value(value)) {
    return {
      message: validation.custom.message,
      type: 'custom'
    };
  }

  return null;
};

const validateFormElement = (
  element: FormElement,
  validation: ElementValidation
): FormError | null => {
  if (!validation || !element) {
    return null;
  }

  const { value } = element;

  const result = validateFormValue(value, validation);

  if (!result) return null;

  return { ...result, element };
};

export const validateFormElements = (
  elements: FormElements,
  validations: Validations
): FormErrors =>
  Object.keys(validations).reduce((errors: FormErrors, name: string): FormErrors => {
    const result = validateFormElement(elements[name], validations[name]);

    return result ? { ...errors, [name]: result } : errors;
  }, {});

export const getNumErrors = (formErrors: FormErrors): number => Object.keys(formErrors).length;

export const focusFirstError = (formErrors: FormErrors, formElementArray: FormElement[]): void => {
  formElementArray.some(element => {
    const elementName = getElementName(element);

    if (formErrors[elementName]) {
      element.focus();

      return true;
    }

    return false;
  });
};
